/**
 * Byte-level primitives shared by the action + seed-contrib envelope
 * encoders. Leaf-level module — no dependency on the rest of the
 * envelope siblings. Hosts:
 *
 *   - `vu64`        — postcard LEB128-style varint over u64
 *   - `concatBytes` — Uint8Array concatenation (avoids repeated alloc)
 *   - `u64LE`       — fixed 8-byte little-endian u64
 *   - `bytesToHex`  — bytes → lowercase hex string
 *   - `hexToBytes`  — hex string → bytes (rejects odd-length / invalid)
 */

/**
 * Postcard varint encoding of an unsigned 64-bit integer.
 * LEB128-style continuation bits — low 7 bits per byte, MSB = 1 means
 * "more bytes follow". Values below 128 fit in a single byte, matching
 * Rust `postcard::to_allocvec(&u64)` output. Accepts JS `bigint` so
 * 64-bit ranges work without precision loss (Number tops out at 2^53).
 */
export function vu64(n: bigint): Uint8Array {
  if (n < 0n || n > (1n << 64n) - 1n) {
    throw new RangeError(`vu64: out of u64 range: ${n}`);
  }
  const out: number[] = [];
  let v = n;
  while (true) {
    const b = Number(v & 0x7fn);
    v >>= 7n;
    if (v > 0n) {
      out.push(b | 0x80);
    } else {
      out.push(b);
      break;
    }
  }
  return new Uint8Array(out);
}

/** Concatenate any number of `Uint8Array` chunks into a single
 *  freshly-allocated buffer. Shared anchor used by every envelope
 *  encoder + signing payload builder; one allocation per call. */
export function concatBytes(...chunks: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const c of chunks) total += c.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

/** Fixed 8-byte little-endian encoding of an unsigned 64-bit integer.
 *  Used by the BLAKE3 signing payloads (hand_seq + action_seq slots)
 *  where the postcard varint shape would break parity with the Rust
 *  `sign_payload` byte order. */
export function u64LE(n: bigint): Uint8Array {
  if (n < 0n || n > (1n << 64n) - 1n) {
    throw new RangeError(`u64LE: out of range: ${n}`);
  }
  const buf = new Uint8Array(8);
  let v = n;
  for (let i = 0; i < 8; i++) {
    buf[i] = Number(v & 0xffn);
    v >>= 8n;
  }
  return buf;
}

export function bytesToHex(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) {
    const v = bytes[i];
    if (v === undefined) continue;
    s += v.toString(16).padStart(2, "0");
  }
  return s;
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) {
    throw new RangeError("hexToBytes: odd-length hex");
  }
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    const byte = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    if (Number.isNaN(byte)) throw new RangeError("hexToBytes: invalid hex");
    out[i] = byte;
  }
  return out;
}
